import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import AdminSelect from "../../components/AdminSelect";
import PasswordInput from "../../components/PasswordInput";

export default function AdminClients() {
  const [venues, setVenues] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const v = await adminApi.getVenues();
      setVenues(v);
      const clientsArr = [];
      for (const venue of v) {
        try {
          const clients = await adminApi.getClients(venue.id);
          (clients || []).forEach((c) => clientsArr.push({ ...c, venueName: venue.name, venueId: venue.id, venueColor: venue.primary_color }));
        } catch (_) {}
      }
      setAllClients(clientsArr);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  const filtered = allClients.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.venueName?.toLowerCase().includes(q);
  });

  async function handleInvite(e) {
    e.preventDefault();
    setError("");
    if (!selectedVenue) { setError("Select a venue"); return; }
    try {
      await adminApi.createClient(selectedVenue, form);
      setForm({ name: "", email: "", password: "" });
      setShowInvite(false);
      setSuccess("Client user created successfully");
      setTimeout(() => setSuccess(""), 3000);
      loadAll();
    } catch (err) { setError(err.message); }
  }

  async function handleResetClientPassword(e) {
    e.preventDefault();
    if (resetPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    try {
      await adminApi.changeClientPassword(resetTarget.venueId, resetTarget.id, resetPassword);
      setResetTarget(null);
      setResetPassword("");
      setSuccess("Client password reset successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
  }

  // unique venues that have at least 1 client
  const venueGroups = venues.filter((v) => filtered.some((c) => c.venueId === v.id));

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="venues-page-header">
        <div>
          <h1 className="venues-page-title">Client Users</h1>
          <p className="page-subtitle">Manage Venue Portal access across all venues</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Invite Client
        </button>
      </div>

      {error && <div className="toast toast-error">{error}</div>}
      {success && <div className="toast toast-success">{success}</div>}

      {/* Summary + search bar */}
      {!loading && (
        <div className="venues-summary">
          <div className="venues-summary-pill">
            <span className="venues-summary-val">{allClients.length}</span>
            <span className="venues-summary-lbl">Total Clients</span>
          </div>
          <div className="venues-summary-divider" />
          <div className="venues-summary-pill">
            <span className="venues-summary-val">{venues.filter((v) => allClients.some((c) => c.venueId === v.id)).length}</span>
            <span className="venues-summary-lbl">Venues covered</span>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search clients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
          </svg>
          <h3>{search ? "No matching clients" : "No client users yet"}</h3>
          <p>{search ? "Try a different search term" : "Invite people to access the Venue Portal"}</p>
          {!search && <button className="btn btn-primary" onClick={() => setShowInvite(true)}>Invite Client</button>}
        </div>
      ) : (
        <div className="venues-table-wrap">
          <div className="venues-table-head">
            <span className="ct-col-user">User</span>
            <span className="ct-col-venue">Venue</span>
            <span className="ct-col-created">Created</span>
            <span className="ct-col-actions">Actions</span>
          </div>
          {filtered.map((c) => (
            <div key={`${c.id}-${c.venueId}`} className="venues-table-row">
              {/* User */}
              <div className="ct-col-user">
                <div className="ct-avatar">
                  {(c.name?.charAt(0) || c.email?.charAt(0) || "?").toUpperCase()}
                </div>
                <div className="ct-user-info">
                  <span className="ct-user-name">{c.name || "Unnamed"}</span>
                  <span className="ct-user-email">{c.email}</span>
                </div>
              </div>

              {/* Venue badge */}
              <div className="ct-col-venue">
                <Link
                  to={`/admin/venues/${c.venueId}`}
                  className="ct-venue-badge"
                  style={{ "--vcolor": c.venueColor || "#3b82f6" }}
                >
                  {c.venueName}
                </Link>
              </div>

              {/* Created */}
              <div className="ct-col-created">
                {c.created_at ? new Date(c.created_at).toLocaleDateString("en-AU") : "—"}
              </div>

              {/* Actions */}
              <div className="ct-col-actions">
                <button
                  className="btn btn-warning btn-sm"
                  onClick={() => { setResetTarget(c); setResetPassword(""); setError(""); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  Reset Password
                </button>
                <Link to={`/portal/${venues.find((v) => v.id === c.venueId)?.slug}`} className="btn btn-purple btn-sm">
                  Portal
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowInvite(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </div>
              <div className="modal-header-text">
                <h2>Invite Client User</h2>
                <p>Grant someone access to manage a venue's members</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowInvite(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleInvite}>
                <div className="form-field">
                  <label>Venue <span className="required">*</span></label>
                  <AdminSelect
                    value={selectedVenue}
                    onChange={(v) => setSelectedVenue(v)}
                    placeholder="Select venue…"
                    searchable
                    searchPlaceholder="Search venues..."
                    options={venues.map((v) => ({ value: String(v.id), label: v.name }))}
                  />
                </div>
                <div className="form-field">
                  <label>Full Name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
                </div>
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" required />
                </div>
                <div className="form-field">
                  <label>Temporary Password <span className="required">*</span></label>
                  <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 characters" required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Create & Invite</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setResetTarget(null)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon" style={{ background: "var(--warning-soft)", color: "var(--warning)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </div>
              <div className="modal-header-text">
                <h2>Reset Password</h2>
                <p>Set a new password for <strong>{resetTarget.name || resetTarget.email}</strong></p>
              </div>
              <button className="modal-close-btn" onClick={() => setResetTarget(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleResetClientPassword}>
                {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
                <div className="form-field">
                  <label>New Password <span className="required">*</span></label>
                  <PasswordInput value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Minimum 6 characters" required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setResetTarget(null)}>Cancel</button>
                  <button type="submit" className="btn btn-warning">Reset Password</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
